import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { usePreferences } from "../hooks/use-preferences";
import { cn } from "@/lib/utils";

export const FavoriteBrandsSection: React.FC = () => {
  const { t } = useTranslation();
  const {
    brands,
    isBrandsLoading,
    brandPreferences,
    isBrandFavorite,
    toggleBrand,
    clearBrandPreferences,
  } = usePreferences();

  const [searchQuery, setSearchQuery] = useState("");

  // Filter brands based on search query
  const filteredBrands = useMemo(() => {
    if (!searchQuery.trim()) return brands;
    const query = searchQuery.toLowerCase();
    return brands.filter((brand) =>
      brand.name.toLowerCase().includes(query)
    );
  }, [brands, searchQuery]);

  const groupedBrands = useMemo(() => {
    const groups: Record<string, typeof filteredBrands> = {};

    const normalizeFirstChar = (name: string): string => {
      const trimmed = (name || "").trim();
      if (!trimmed) return "#";
      const ch = trimmed[0].toLocaleUpperCase("tr-TR");
      // Group numbers/symbols under '#'
      return /[A-ZÇĞİÖŞÜ]/i.test(ch) ? ch : "#";
    };

    filteredBrands.forEach((b) => {
      const key = normalizeFirstChar(b.name);
      if (!groups[key]) groups[key] = [];
      groups[key].push(b);
    });

    // Sort groups and items
    Object.keys(groups).forEach((k) => {
      groups[k] = groups[k].sort((a, b) =>
        a.name.localeCompare(b.name, "tr-TR", { sensitivity: "base" })
      );
    });

    const orderedKeys = Object.keys(groups).sort((a, b) => {
      if (a === "#") return 1;
      if (b === "#") return -1;
      return a.localeCompare(b, "tr-TR");
    });

    return { groups, orderedKeys };
  }, [filteredBrands]);

  return (
    <div className="flex flex-col gap-6">
      {/* Section Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <h3 className="text-base md:text-lg font-semibold text-text-primary dark:text-text-primary">
              {t("preferences.favoriteBrands.title")}
            </h3>
            <p className="text-xs md:text-sm text-text-tertiary dark:text-text-tertiary">
              {t("preferences.favoriteBrands.description")}
            </p>
          </div>
          {brandPreferences.length > 0 && (
            <button
              onClick={clearBrandPreferences}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs md:text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              <X className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span>{t("preferences.favoriteBrands.clearBrands")}</span>
            </button>
          )}
        </div>
        {brandPreferences.length > 0 && (
          <p className="text-xs md:text-sm text-indigo-600 dark:text-indigo-400 font-medium">
            {t("preferences.favoriteBrands.brandsSelected", {
              count: brandPreferences.length,
            })}
          </p>
        )}
      </div>

      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          <Search className="h-4 w-4 text-text-tertiary dark:text-text-tertiary" />
        </div>
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("preferences.favoriteBrands.searchBrands")}
          className="w-full pl-10 pr-10 py-2 bg-white dark:bg-gray-900 border border-border-secondary dark:border-border-secondary rounded-lg text-sm text-text-secondary dark:text-gray-100 placeholder:text-text-tertiary dark:placeholder:text-gray-400 focus:ring-0 focus:border-indigo-400 dark:focus:border-indigo-400"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2"
            aria-label={t("common.clear")}
          >
            <X className="h-4 w-4 text-text-tertiary dark:text-text-tertiary hover:text-text-secondary dark:hover:text-gray-300" />
          </button>
        )}
      </div>

      {/* Brands Grid */}
      <div className="border border-border-secondary dark:border-border-secondary rounded-xl p-4 md:p-6 bg-white dark:bg-gray-800">
        {/* Scrollable List Area */}
        <div className="max-h-[360px] md:max-h-[520px] overflow-y-auto pr-1">
          {isBrandsLoading ? (
            <div className="py-8 text-sm text-text-tertiary dark:text-text-tertiary">
              {t("common.loading")}
            </div>
          ) : filteredBrands.length > 0 ? (
            <div className="flex flex-col gap-6">
              {/* When searching, still keep grouping but it will be sparse */}
              {groupedBrands.orderedKeys.map((letter) => (
                <div key={letter} className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-bg-tertiary dark:bg-gray-700 flex items-center justify-center">
                        <span className="text-sm font-bold text-text-secondary dark:text-gray-200">
                          {letter}
                        </span>
                      </div>
                      <span className="text-xs md:text-sm font-semibold text-text-tertiary dark:text-text-tertiary">
                        {groupedBrands.groups[letter].length}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    {groupedBrands.groups[letter].map((brand) => {
                      const isFavorite = isBrandFavorite(brand.id);
                      return (
                        <label
                          key={brand.id}
                          className={cn(
                            "flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors",
                            isFavorite
                              ? "border-indigo-400 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20"
                              : "border-border-secondary dark:border-border-secondary bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-500"
                          )}
                        >
                          <Checkbox
                            checked={isFavorite}
                            onCheckedChange={() =>
                              toggleBrand({ id: brand.id, name: brand.name })
                            }
                            className="border-indigo-400 dark:border-indigo-400"
                          />
                          <span
                            className={cn(
                              "text-sm font-medium flex-1",
                              isFavorite
                                ? "text-indigo-600 dark:text-indigo-400"
                                : "text-text-secondary dark:text-gray-300"
                            )}
                          >
                            {brand.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 md:py-12">
              <p className="text-sm md:text-base text-text-tertiary dark:text-text-tertiary text-center">
                {searchQuery
                  ? t("instantSearch.noResults")
                  : t("preferences.favoriteBrands.noBrandsSelected")}
              </p>
              {searchQuery && (
                <p className="text-xs md:text-sm text-text-quaternary dark:text-gray-500 text-center mt-1">
                  {t("instantSearch.tryDifferentSearch")}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

